import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  BookOpen, 
  Play, 
  Clock, 
  Award,
  ChevronRight,
  GraduationCap,
  Target,
  TrendingUp,
  Users,
  Video,
  FileText,
  Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  BookOpen: BookOpen,
  GraduationCap: GraduationCap,
  Target: Target,
  TrendingUp: TrendingUp,
  Users: Users,
  Video: Video,
  FileText: FileText,
  Star: Star
};

const difficultyColors = {
  beginner: "bg-emerald-100 text-emerald-700",
  intermediate: "bg-amber-100 text-amber-700",
  advanced: "bg-red-100 text-red-700"
};

export default function LearningCenterPage({ user }) {
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [myProgress, setMyProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const [catRes, progressRes] = await Promise.all([
        fetch(`${API}/learning/categories`, { credentials: "include" }),
        fetch(`${API}/learning/my-progress`, { credentials: "include" })
      ]);

      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData.categories || []);
      }

      if (progressRes.ok) {
        const progressData = await progressRes.json();
        setMyProgress(progressData);
      }
    } catch (error) {
      console.error("Failed to fetch learning data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      let url = `${API}/learning/courses`;
      if (selectedCategory !== "all") {
        url += `?category_id=${selectedCategory}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      }
    } catch (error) {
      console.error("Failed to fetch courses:", error);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="learning-center-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-blue-600 rounded-2xl p-8 text-white">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <GraduationCap className="w-8 h-8" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold">Sales Learning Center</h1>
            <p className="text-white/80">Master sales skills and platform features</p>
          </div>
        </div>
        
        {myProgress && (myProgress.total_completed > 0 || myProgress.total_in_progress > 0) && (
          <div className="flex gap-6 mt-6">
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="text-2xl font-bold">{myProgress.total_completed}</p>
              <p className="text-sm text-white/70">Completed</p>
            </div>
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="text-2xl font-bold">{myProgress.total_in_progress}</p>
              <p className="text-sm text-white/70">In Progress</p>
            </div>
          </div>
        )}
      </div>

      {/* Continue Learning */}
      {myProgress?.courses_in_progress?.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-primary" />
            Continue Learning
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myProgress.courses_in_progress.slice(0, 3).map((course) => (
              <Card 
                key={course.course_id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/learning/courses/${course.course_id}`)}
              >
                <CardContent className="p-4">
                  <h3 className="font-medium mb-2 line-clamp-1">{course.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <span>{course.completed_lessons}/{course.total_lessons} lessons</span>
                  </div>
                  <Progress value={course.progress_percent} className="h-2" />
                  <p className="text-xs text-slate-500 mt-2">
                    {course.progress_percent}% complete
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-bold mb-4">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category) => {
              const Icon = categoryIcons[category.icon] || BookOpen;
              return (
                <Card 
                  key={category.category_id}
                  className={`hover:shadow-lg transition-all cursor-pointer ${
                    selectedCategory === category.category_id ? "ring-2 ring-primary" : ""
                  }`}
                  onClick={() => setSelectedCategory(
                    selectedCategory === category.category_id ? "all" : category.category_id
                  )}
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-medium">{category.name}</h3>
                    <p className="text-sm text-slate-500">{category.course_count || 0} courses</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* All Courses */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">
            {selectedCategory === "all" ? "All Courses" : "Filtered Courses"}
          </h2>
          {selectedCategory !== "all" && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedCategory("all")}>
              Clear filter
            </Button>
          )}
        </div>

        {courses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="font-medium text-slate-900 mb-2">No Courses Available</h3>
              <p className="text-slate-500">
                {user?.system_role === "platform_owner" 
                  ? "Get started by creating your first course."
                  : "Check back soon for new learning content!"}
              </p>
              {user?.system_role === "platform_owner" && (
                <Button className="mt-4" onClick={() => navigate("/learning/admin")}>
                  Create Course
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Card 
                key={course.course_id}
                className="hover:shadow-lg transition-shadow overflow-hidden group"
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                  {course.thumbnail_url ? (
                    <img 
                      src={course.thumbnail_url} 
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <Button 
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => navigate(`/learning/courses/${course.course_id}`)}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Learning
                    </Button>
                  </div>
                  {!course.is_published && (
                    <Badge className="absolute top-2 right-2 bg-amber-500">Draft</Badge>
                  )}
                </div>
                
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={difficultyColors[course.difficulty]}>
                      {course.difficulty}
                    </Badge>
                    {course.duration_minutes > 0 && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(course.duration_minutes)}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                    {course.title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                    {course.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">
                      {course.lesson_count || 0} lessons
                    </span>
                    {course.progress_percent > 0 && (
                      <div className="flex items-center gap-2">
                        <Progress value={course.progress_percent} className="w-20 h-2" />
                        <span className="text-xs text-slate-500">{course.progress_percent}%</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Admin Link */}
      {user?.system_role === "platform_owner" && (
        <Card className="bg-slate-50 border-dashed">
          <CardContent className="py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Award className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Content Management</p>
                <p className="text-sm text-slate-500">Create and manage courses, lessons, and categories</p>
              </div>
            </div>
            <Button onClick={() => navigate("/learning/admin")}>
              Manage Content
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
